import { injectAesthetic } from './shared-styles.ts';

export class AppBadge extends HTMLElement {
    static get observedAttributes() {
        return ['variant', 'size', 'pill', 'unshadowed', 'color'];
    }

    private seed: number;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
        this.seed = Math.floor(Math.random() * 10000);
    }

    connectedCallback() {
        this.render();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            this.render();
        }
    }

    render() {
        if (!this.shadowRoot) return;

        const variant = this.getAttribute('variant') || 'neutral';
        const size = this.getAttribute('size') || 'md';
        const pill = this.hasAttribute('pill');
        const unshadowed = this.hasAttribute('unshadowed');
        const color = this.getAttribute('color');

        let customStyle = '';
        if (color) {
            // If color is hex or css variable, we'll try to apply it as text color and subtle bg
            // Note: simple fallback style
            customStyle = `color: ${color}; border-color: ${color}; opacity: 0.9;`;
        }

        this.shadowRoot.innerHTML = `
            ${injectAesthetic(this.seed)}
            <style>
                :host {
                    display: inline-block;
                    vertical-align: middle;
                }

                .badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-family: var(--font-main);
                    font-weight: 500;
                    line-height: 1;
                    white-space: nowrap;
                    border: 1px solid transparent;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                }

                /* Sizes */
                .badge.sm { padding: 0.2rem 0.4rem; font-size: 0.6rem; border-radius: var(--radius-sm); }
                .badge.md { padding: 0.25rem 0.6rem; font-size: 0.75rem; border-radius: var(--radius-sm); }
                .badge.lg { padding: 0.35rem 0.8rem; font-size: 0.8rem; border-radius: var(--radius-sm); }

                /* Pill shape override */
                .badge.pill { border-radius: var(--radius-full); }

                /* Variants */
                .badge.neutral {
                    background: var(--bg-card);
                    color: var(--text-muted);
                    border-color: var(--border);
                }

                .badge.success {
                    background: rgba(16, 185, 129, 0.1);
                    color: #10b981;
                    border-color: rgba(16, 185, 129, 0.3);
                }

                .badge.warning {
                    background: var(--accent-soft);
                    color: var(--accent);
                    border-color: var(--border-accent);
                }

                .badge.danger {
                    background: rgba(239, 68, 68, 0.1);
                    color: #ef4444;
                    border-color: rgba(239, 68, 68, 0.3);
                }
                
                .badge.info {
                    background: rgba(59, 130, 246, 0.1);
                    color: #3b82f6;
                    border-color: rgba(59, 130, 246, 0.3);
                }
            </style>
            <span class="badge ${color ? '' : variant} ${size} ${pill ? 'pill' : ''} ${unshadowed ? '' : 'shadow-industrial'}" style="${customStyle}">
                <slot></slot>
            </span>
        `;
    }
}

customElements.define('app-badge', AppBadge);
